<template>
  <PForm
    nested
    :schema="r$"
    :validate-on="['blur', 'input']"
    class="model-edit-form-sale-item flex flex-col"
  >
    <div class="primary-lighten-1 flex w-full flex-col gap-y-2 rounded-md p-2">
      <PFormField sr-only label="Artikelnaam" name="name">
        <PInput v-model="saleItem.name" ref="inputName" />
      </PFormField>

      <PFormField sr-only label="Eenheid" name="unit">
        <PInputComboboxUnit v-model="saleItem.unit" input-placeholder="Eenheid" />
      </PFormField>

      <ModelEditFormProjectSaleItem
        v-if="creationParameters?.project"
        :project="creationParameters.project"
        :sale-item="saleItem"
        @input="anyPriceWasChanged = true"
        ref="modelEditFormProjectSaleItem"
      />

      <ModelEditFormKnownBusinessEntitySaleItem
        v-if="creationParameters.project"
        :project="creationParameters.project"
        :sale-item="saleItem"
        @input="anyPriceWasChanged = true"
        ref="modelEditFormKnownBusinessEntitySaleItem"
      />

      <PFormField label="Eenheidsprijs">
        <PInputNumber
          v-model="saleItem.unitPrice"
          placeholder="Eenheidsprijs"
          allow-zero
          @input="anyPriceWasChanged = true"
        >
          <span class="sm:text-sm">&euro;</span>
        </PInputNumber>
      </PFormField>

      <PFormField
        label="Brandstoftoeslagfactor"
        help="Leeg = geen toeslag, 1 = onaangepaste toeslag"
      >
        <PInputNumber
          v-model="saleItem.surchargeModifier"
          placeholder="Brandstoftoeslagfactor"
          allow-zero
        />
      </PFormField>

      <ModelEditFormRetroactivePricing
        v-if="!isCreating && anyPriceWasChanged"
        v-model="retroactiveDate"
        price-type="sale"
      >
        <template #subtitle>
          <span class="block text-xs">
            Alle verkopen <b>zonder productieverantwoording</b> vanaf deze datum worden aangepast
          </span>
        </template>
      </ModelEditFormRetroactivePricing>
    </div>
    <PAlertFormValidation :model-value="r$" />
  </PForm>
</template>

<script setup lang="ts">
import PForm from "@/components/form/PForm.vue"
import PAlertFormValidation from "@/components/form/PAlertFormValidation.vue"
import { computed, watchEffect, inject, onMounted, ref } from "vue"
import { useRegle } from "@regle/core"
import PFormField from "@/components/form/PFormField.vue"
import PInput from "@/components/form/PInput.vue"
import PInputNumber from "@/components/form/PInputNumber.vue"
import { SaleItem } from "@/models/sale-item.model"
import { requiredValidator } from "@/helper/validation/validators"
import type { SaleItemController } from "@/controllers/model-controllers/sale-item-controller"
import { formFocusHack } from "@/helper/form-helper"
import { useFormConfigurator } from "@/composables/form/form-configurator"
import { upperFirst } from "lodash-es"
import PInputComboboxUnit from "@/components/form/PInputComboboxUnit.vue"
import { Project } from "@/models/project.model"
import ModelEditFormProjectSaleItem from "@/components/model_edit_form/ModelEditFormProjectSaleItem.vue"
import ModelEditFormKnownBusinessEntitySaleItem from "@/components/model_edit_form/ModelEditFormKnownBusinessEntitySaleItem.vue"
import ModelEditFormRetroactivePricing from "@/components/model_edit_form/ModelEditFormRetroactivePricing.vue"
import SaleController from "@/controllers/model-controllers/sale-controller"
import { uniqueSaleItemValidator } from "@/helper/validation/sale-item-validation.ts"

const inputName = ref()

const controller: SaleItemController = inject("saleItemController")!

const {
  model: saleItem,
  initialText,
  isCreating,
  isFormInvalid
} = useFormConfigurator<SaleItem>({
  controller,
  customSaveHandler
})

const creationParameters: { project?: Project } = inject("creationParameters")!

onMounted(() => {
  if (initialText) {
    saleItem.value.name = upperFirst(initialText)
  }
  formFocusHack(inputName, saleItem, "name")
})

const saleController: SaleController = inject("saleController")!
const modelEditFormProjectSaleItem = ref()
const modelEditFormKnownBusinessEntitySaleItem = ref()
async function customSaveHandler(save: () => Promise<void>) {
  await save()
  if (creationParameters.project) {
    await Promise.all([
      modelEditFormProjectSaleItem.value.savePriceAgreement(),
      modelEditFormKnownBusinessEntitySaleItem.value.savePriceAgreement()
    ])
  }
  if (retroactiveDate.value) {
    await saleController.applyPricesRetroactively(saleItem.value, retroactiveDate.value)
  }
}

const anyPriceWasChanged = ref(false)
const retroactiveDate = ref()
const rules = {
  name: { requiredValidator },
  unit: { requiredValidator },
  $self: { unique: uniqueSaleItemValidator(saleItem) }
}

const { r$ } = useRegle(
  computed(() => ({ name: saleItem.value.name, unit: saleItem.value.unit })),
  rules,
  { autoDirty: false }
)

watchEffect(() => {
  isFormInvalid.value = r$.$invalid
})
</script>

<style scoped></style>
